import './TagsSection.scss';
import React, {useState, useMemo, useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrFilterChip from '../../../../common/arr-filter-chip/ArrFilterChip';
import TagsTreePanel from '../tags-tree-panel/TagsTreePanel';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {TagItem} from '../../../../../api/types/production.type';
import {t} from 'i18next';
import {
  hasEnabledChild,
  filterValidTags,
} from '../../CreateNewProductionHelpers';

interface TagsSectionProps {
  headerTitle?: string;
  buttonTitle?: string;
  selectedTags: TagItem[];
  onTagsChange: (tags: TagItem[]) => void;
  tagsTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  enabledForType?: string;
  disabledTags?: TagItem[];
  onAddTagsClick?: () => void;
  moreItemSection?: React.ReactNode;
  disabled?: boolean;
}

const TagsSection: React.FC<TagsSectionProps> = ({
  headerTitle = t(
    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.labels.tags'
  ),
  buttonTitle = t(
    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.labels.addTags'
  ),
  selectedTags,
  onTagsChange,
  tagsTreeData = [],
  enabledForType = '',
  disabledTags = [],
  onAddTagsClick,
  moreItemSection,
  disabled = false,
}) => {
  const [showTagsPanel, setShowTagsPanel] = useState(false);

  // Conversion functions
  const convertTagItemToTreeView = (
    tag: TagItem
  ): TreeViewBaseItem<ExtendedTreeItemProps> => ({
    id: String(tag.tagId),
    label: tag.tagLabel,
  });

  const convertTreeViewToTagItem = (
    item: TreeViewBaseItem<ExtendedTreeItemProps>
  ): TagItem => ({
    tagId: parseInt(item.id),
    tagLabel: item.label,
  });

  // Process tree data to enable/disable items based on enabledForType and disabledTags
  // This matches the logic in PrivGuard's transformTagTreeData to handle parent groups correctly
  const processTreeData = (
    tree: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    const disabledTagIds = new Set(
      disabledTags.map((tag) => String(tag.tagId))
    );

    // First pass: process tree and only disable leaf nodes initially
    const processedTree = tree.map((item) => {
      const isDisabledByType =
        enabledForType !== '' && item.type !== enabledForType;
      const isDisabledByTags = disabledTagIds.has(item.id);
      // Only disable leaf nodes (non-groups) initially
      // Parent groups will be handled in second pass
      const isDisabled =
        (isDisabledByType || isDisabledByTags) && !item.isGroup;

      return {
        ...item,
        disabled: isDisabled,
        children: item.children ? processTreeData(item.children) : undefined,
      };
    });

    // Second pass: disable parent groups if they have no enabled children
    // Using hasEnabledChild utility function

    const processNode = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
      if (node.isGroup && enabledForType !== '') {
        // Only disable if it has no enabled children
        if (!hasEnabledChild(node)) {
          node.disabled = true;
        }
      }
      // Process children recursively
      if (node.children) {
        node.children.forEach(processNode);
      }
    };

    processedTree.forEach(processNode);

    return processedTree;
  };

  const processedTagsTreeData = useMemo(
    () => processTreeData(tagsTreeData),
    [tagsTreeData, enabledForType, disabledTags]
  );

  // Filter initial selectedTags to only include valid tags
  const filteredSelectedTags = useMemo(() => {
    return filterValidTags(selectedTags, tagsTreeData);
  }, [selectedTags, tagsTreeData]);

  const handleRemoveTag = (tagId: number) => {
    if (disabled) return;
    const updatedTags = filteredSelectedTags.filter(
      (tag) => tag.tagId !== tagId
    );
    onTagsChange(updatedTags);
  };

  const handleTagsSelect = (
    items: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ) => {
    const newTags = items.map(convertTreeViewToTagItem);
    // Filter tags before updating to ensure only valid tags are included
    const validTags = filterValidTags(newTags, tagsTreeData);
    onTagsChange(validTags);
    setShowTagsPanel(false);
  };

  const handleAddTags = () => {
    if (disabled) return;
    if (onAddTagsClick) {
      // If a parent handler is provided, use it for special cases (like closing a modal).
      onAddTagsClick();
    } else {
      // Otherwise, use the default behavior and open the panel internally.
      setShowTagsPanel(true);
    }
  };

  // Filter and validate tags when tagsTreeData becomes available
  // Only update if tagsTreeData is available and we have selectedTags to validate
  useEffect(() => {
    // Only run validation when tagsTreeData is available (not empty)
    // This prevents clearing tags when component mounts before tagTreeData loads
    if (tagsTreeData && tagsTreeData.length > 0 && selectedTags.length > 0) {
      const validTags = filterValidTags(selectedTags, tagsTreeData);
      // Only update if the filtered tags are different from current selectedTags
      // This prevents unnecessary re-renders and state updates
      if (validTags.length !== selectedTags.length) {
        onTagsChange(validTags);
      }
    }
  }, [tagsTreeData, selectedTags, onTagsChange]);

  return (
    <>
      <Box className="tags-section">
        <Box className="tags-header">
          <Typography
            className="typo-text subhead5_size tags-label"
            data-testid={`tags-section-header-title-${enabledForType}`}
          >
            {headerTitle}
          </Typography>
          <ArrButton
            variant="primary"
            size="small"
            className="add-tags-btn typo-text buttonSmall_size"
            onClick={handleAddTags}
            disabled={disabled}
            dataTestId={`tags-section-button-${enabledForType}`}
          >
            {buttonTitle}
          </ArrButton>
        </Box>
        {/* Selected Tags Display */}
        <Box className="selected-tags-display">
          {filteredSelectedTags &&
            filteredSelectedTags.length > 0 &&
            filteredSelectedTags.map((tag, index) => (
              <ArrFilterChip
                key={tag.tagId}
                classNames="arr-filter-chip-v2-radius"
                filterName=""
                selections={[
                  {
                    id: String(tag.tagId),
                    value: tag.tagLabel,
                  },
                ]}
                variant="v1"
                isResponsive={false}
                onRemove={
                  disabled ? undefined : () => handleRemoveTag(tag.tagId)
                }
                disabled={disabled}
                dataTestId={`filtered-selected-tags-chip-${index}`}
              />
            ))}
        </Box>
      </Box>
      {/* Tags Tree Panel */}
      <TagsTreePanel
        type="flyout"
        isOpen={showTagsPanel}
        onClose={() => setShowTagsPanel(false)}
        tags={processedTagsTreeData}
        selectedTags={filteredSelectedTags.map(convertTagItemToTreeView)}
        setSelectedTags={handleTagsSelect}
        moreItemSection={moreItemSection}
      />
    </>
  );
};

export default TagsSection;
